import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import JemaatList from "@/pages/JemaatList";
import JemaatDetail from "@/pages/JemaatDetail";
import Keputusan from "@/pages/Keputusan";
import SuratMasuk from "@/pages/SuratMasuk";
import SuratKeluar from "@/pages/SuratKeluar";
import Kalender from "@/pages/Kalender";
import Pengurus from "@/pages/Pengurus";
import Iuran from "@/pages/Iuran";
import Laporan from "@/pages/Laporan";
import Arsip from "@/pages/Arsip";
import ProgramKerja from "@/pages/ProgramKerja";
import PanitiaKantor from "@/pages/PanitiaKantor";
import KartuPengurus from "@/pages/KartuPengurus";
import WidgetSync from "@/pages/WidgetSync";
import UndanganJemaat from "@/pages/UndanganJemaat";
import BukuKas from "@/pages/BukuKas";
import BinaUsaha from "@/pages/BinaUsaha";
import LaporanRAKER from "@/pages/LaporanRAKER";
import ReminderIbadah from "@/pages/ReminderIbadah";
import ImportAnggota from "@/pages/ImportAnggota";
import ManajemenPengguna from "@/pages/ManajemenPengguna";
import ForgotPassword from "@/pages/ForgotPassword";
import BroadcastPengumuman from "@/pages/BroadcastPengumuman";
import AuditLog from "@/pages/AuditLog";
import NotifikasiPush from "@/pages/NotifikasiPush";
import KartuAnggota from "@/pages/KartuAnggota";
import VerifyMember from "@/pages/VerifyMember";
import AnggaranRealisasi from "@/pages/AnggaranRealisasi";
import Kegiatan from "@/pages/Kegiatan";
import KalenderIbadah from "@/pages/KalenderIbadah";
import BidangKomisi from "@/pages/BidangKomisi";
import Albums from "@/pages/Albums";
import AlbumDetail from "@/pages/AlbumDetail";
import PublicPortal from "@/pages/PublicPortal";
import PublicStatistik from "@/pages/PublicStatistik";
import PublicAlbum from "@/pages/PublicAlbum";
import Placeholder from "@/pages/Placeholder";
import GantiSandi from "@/pages/GantiSandi";
import AdminSystem from "@/pages/AdminSystem";
import VerifikasiData from "@/pages/VerifikasiData";
import Aset from "@/pages/Aset";
import { NotFound, Forbidden } from "@/pages/Errors";
import SplashScreen from "@/components/SplashScreen";

function RootRedirect() {
  const { user, checking } = useAuth();
  if (checking) return null;
  return <Navigate to={user ? "/dashboard" : "/public"} replace />;
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <ThemeProvider>
      <BrowserRouter>
        <SplashScreen duration={1500} />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/public" element={<PublicPortal />} />
          <Route path="/public/statistik" element={<PublicStatistik />} />
          <Route path="/public/album/:slug" element={<PublicAlbum />} />
          <Route path="/403" element={<Forbidden />} />

          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/jemaat" element={<ProtectedRoute><JemaatList /></ProtectedRoute>} />
          <Route path="/jemaat/verifikasi" element={<ProtectedRoute><VerifikasiData /></ProtectedRoute>} />
          <Route path="/jemaat/:id" element={<ProtectedRoute><JemaatDetail /></ProtectedRoute>} />
          <Route path="/keputusan" element={<ProtectedRoute><Keputusan /></ProtectedRoute>} />
          <Route path="/tindak-lanjut" element={<ProtectedRoute><Keputusan /></ProtectedRoute>} />
          <Route path="/surat-masuk" element={<ProtectedRoute><SuratMasuk /></ProtectedRoute>} />
          <Route path="/kalender" element={<ProtectedRoute><Kalender /></ProtectedRoute>} />

          {/* Placeholder modules for phase 2+ */}
          <Route path="/pengurus" element={<ProtectedRoute><Pengurus /></ProtectedRoute>} />
          <Route path="/iuran" element={<ProtectedRoute><Iuran /></ProtectedRoute>} />
          <Route path="/laporan" element={<ProtectedRoute><Laporan /></ProtectedRoute>} />
          <Route path="/arsip" element={<ProtectedRoute><Arsip /></ProtectedRoute>} />
          <Route path="/kartu/:id" element={<KartuPengurus />} />
          <Route path="/admin/widget" element={<ProtectedRoute><WidgetSync /></ProtectedRoute>} />
          <Route path="/admin/undangan" element={<ProtectedRoute><UndanganJemaat /></ProtectedRoute>} />
          <Route path="/buku-kas" element={<ProtectedRoute><BukuKas /></ProtectedRoute>} />
          <Route path="/bina-usaha" element={<ProtectedRoute><BinaUsaha /></ProtectedRoute>} />
          <Route path="/laporan-raker" element={<ProtectedRoute><LaporanRAKER /></ProtectedRoute>} />
          <Route path="/admin/reminder-ibadah" element={<ProtectedRoute><ReminderIbadah /></ProtectedRoute>} />
          <Route path="/anggota/import" element={<ProtectedRoute><ImportAnggota /></ProtectedRoute>} />
          <Route path="/admin/pengguna" element={<ProtectedRoute><ManajemenPengguna /></ProtectedRoute>} />
          <Route path="/admin/broadcast" element={<ProtectedRoute><BroadcastPengumuman /></ProtectedRoute>} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/kartu-anggota/:id" element={<ProtectedRoute><KartuAnggota /></ProtectedRoute>} />
          <Route path="/verify/:number" element={<VerifyMember />} />
          <Route path="/anggaran-realisasi" element={<ProtectedRoute><AnggaranRealisasi /></ProtectedRoute>} />
          <Route path="/keuangan" element={<ProtectedRoute><Placeholder section="Keuangan" title="Dashboard Keuangan" subtitle="Anggaran vs realisasi, transaksi, dan LPJ keuangan." /></ProtectedRoute>} />
          <Route path="/aset" element={<ProtectedRoute><Aset /></ProtectedRoute>} />
          <Route path="/bidang" element={<ProtectedRoute><BidangKomisi /></ProtectedRoute>} />
          <Route path="/program" element={<ProtectedRoute><ProgramKerja /></ProtectedRoute>} />
          <Route path="/panitia-kantor" element={<ProtectedRoute><PanitiaKantor /></ProtectedRoute>} />
          <Route path="/kegiatan" element={<ProtectedRoute><Kegiatan /></ProtectedRoute>} />
          <Route path="/kalender-ibadah" element={<ProtectedRoute><KalenderIbadah /></ProtectedRoute>} />
          <Route path="/album" element={<ProtectedRoute><Albums /></ProtectedRoute>} />
          <Route path="/album/:id" element={<ProtectedRoute><AlbumDetail /></ProtectedRoute>} />
          <Route path="/rapat" element={<ProtectedRoute><Placeholder section="Rapat & Keputusan" title="Rapat & Sidang" subtitle="Agenda, undangan, notulen, dan keputusan rapat." /></ProtectedRoute>} />
          <Route path="/surat-keluar" element={<ProtectedRoute><SuratKeluar /></ProtectedRoute>} />
          <Route path="/pengumuman" element={<ProtectedRoute><BroadcastPengumuman /></ProtectedRoute>} />
          <Route path="/admin/audit" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
          <Route path="/notifikasi-push" element={<ProtectedRoute><NotifikasiPush /></ProtectedRoute>} />
          <Route path="/ganti-sandi" element={<ProtectedRoute><GantiSandi /></ProtectedRoute>} />
          <Route path="/admin/system" element={<ProtectedRoute><AdminSystem /></ProtectedRoute>} />
          <Route path="/forgot" element={<Placeholder section="Auth" title="Reset Kata Sandi" subtitle="Fitur reset kata sandi akan diaktifkan pada fase berikutnya." />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
